import type { Signal } from "@builder.io/qwik";
import { component$, Slot, useContextProvider } from "@builder.io/qwik";
import { type DocumentHead } from "@builder.io/qwik-city";
import { Footer } from "~/components/ui/footer";
import { Header } from "~/components/ui/header";
import { CoachProfileContext } from "~/contexts/coach";
import type {
  CoachDetailsSelect,
  CoachSelect,
  ProfileSelect,
} from "../../../drizzle/schema";
import { isNotNull } from "~/lib/utils";

import { useCoachData } from "~/shared/loaders";
export { useCoachData } from "~/shared/loaders";

export const head: DocumentHead = ({ head, resolveValue }) => {
  const coach = resolveValue(useCoachData);

  const thisTitle = [coach.fullName, coach.profile?.coachingTitle]
    .filter(isNotNull)
    .join(" | ");
  const title = head.title ? `${head.title} | ${thisTitle}` : thisTitle;

  const description = coach.profile?.description;

  return {
    title,
    meta: [
      {
        property: "og:title",
        content: title,
      },
      ...(description
        ? [
            {
              name: "description",
              content: description,
            },
            {
              property: "og:description",
              content: description,
            },
          ]
        : []),
      ...(coach.profile?.heroImageSrc
        ? [
            {
              property: "og:image",
              content: coach.profile.heroImageSrc,
            },
            {
              property: "twitter:image",
              content: coach.profile.heroImageSrc,
            },
          ]
        : []),
      ...(coach.profilePictureUrl
        ? [
            {
              property: "og:image:alt",
              content: `Profile picture of ${coach.fullName}`,
            },
          ]
        : []),
      {
        name: "author",
        content: coach.fullName,
      },
      ...(coach.profile?.primaryCity
        ? [
            {
              name: "geo.placename",
              content: coach.profile.primaryCity,
            },
          ]
        : []),
      ...(coach.profile?.languages
        ? [
            {
              name: "language",
              content: coach.profile.languages,
            },
          ]
        : []),
    ],
  };
};

export default component$(() => {
  const coach = useCoachData() as Signal<
    Readonly<
      CoachSelect & { profile: ProfileSelect; details: CoachDetailsSelect }
    >
  >;

  useContextProvider(CoachProfileContext, coach);

  return (
    <div class="flex min-h-screen flex-col justify-between overflow-hidden bg-white">
      <Header />

      <div class="z-10 grow">
        <Slot />
      </div>

      <Footer />
    </div>
  );
});
